import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

type HeaderProps = {
  title: string;
  icon: IconProp;
  buttonText?: string;
  onButtonClick?: () => void;
  actions?: React.ReactNode;
  /** Bouton « ? » du tutoriel de la page, affiché à côté du titre (cf. components/tour). */
  help?: React.ReactNode;
  /** Ancre du tutoriel : posée sur l'en-tête lui-même, un conteneur l'empêcherait de rester collé. */
  id?: string;
};

/**
 * Bouton d'action de l'en-tête : icône et libellé sur ordinateur, icône seule dans un
 * petit bouton carré sur mobile (le libellé reste en infobulle et pour les lecteurs d'écran).
 */
export const HeaderAction = ({
  icon,
  label,
  onClick,
  variant = "tertiary",
}: {
  icon: IconProp;
  label: string;
  onClick: () => void;
  variant?: "tertiary" | "secondary";
}) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`flex items-center justify-center gap-2 w-8 h-8 md:w-auto md:h-auto md:px-6 md:py-2 rounded-lg font-oxaniumMedium uppercase tracking-wider text-secondary-text hover:opacity-90 transition-opacity ${
      variant === "tertiary" ? "bg-tertiary" : "bg-secondary"
    }`}
  >
    <FontAwesomeIcon icon={icon} />
    <span className="hidden md:inline">{label}</span>
  </button>
);

/** Vrai quand l'élément `sticky top-0` est collé en haut de l'écran après défilement. */
const useIsStuck = (ref: React.RefObject<HTMLElement>) => {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const update = () =>
      setIsStuck(
        window.scrollY > 0 &&
          (ref.current?.getBoundingClientRect().top ?? 1) <= 0
      );
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [ref]);

  return isStuck;
};

export const Header = ({
  title,
  icon,
  buttonText,
  onButtonClick,
  actions,
  help,
  id,
}: HeaderProps) => {
  const ref = useRef<HTMLElement>(null);
  const isStuck = useIsStuck(ref);

  return (
    // Sur mobile l'en-tête reste visible au défilement, en version compacte ; une fois
    // collé en haut il prend toute la largeur de l'écran (les marges négatives
    // compensent le padding du parent, quel qu'il soit).
    <header
      ref={ref}
      id={id}
      className={`sticky top-0 z-20 md:static shadow-sm md:shadow-none bg-grey-light px-3 py-2 md:px-6 md:py-4 flex flex-wrap justify-between items-center gap-2 md:gap-3 ${
        isStuck
          ? "w-screen ml-[calc(50%-50vw)] rounded-none md:w-auto md:ml-0 md:rounded-lg"
          : "rounded-lg"
      }`}
    >
      <div className="flex flex-row items-center gap-3 md:gap-6 min-w-0">
        <FontAwesomeIcon icon={icon} className="text-lg md:text-2xl" />
        <h1 className="text-lg md:text-2xl font-oxaniumMedium text-default-text">
          {title}
        </h1>
        {help}
      </div>

      {actions ? (
        <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
          {actions}
        </div>
      ) : (
        buttonText &&
        onButtonClick && (
          <HeaderAction
            icon="plus"
            label={buttonText}
            onClick={onButtonClick}
          />
        )
      )}
    </header>
  );
};
